import type { FundSummary, ResearchStatus } from '@nemo/api';
import { LEAD_LABELS, STAGE_LABELS, formatCheque } from '@nemo/core/model';

/** More than this in one click is a mistake, not a batch; the api agrees. */
export const MAX_FUNDS_PER_RESEARCH = 50;

export function isActive(status: ResearchStatus | undefined): boolean {
  return status?.status === 'queued' || status?.status === 'running';
}

export function hasActiveJob(statuses: readonly ResearchStatus[]): boolean {
  return statuses.some((status) => isActive(status));
}

export function researchText(status: ResearchStatus | undefined): string {
  if (status === undefined) return '';
  return status.pendingProposals === 0
    ? status.status
    : `${status.status} (${status.pendingProposals})`;
}

/**
 * A proposal names a database column; the grid shows a cell. Cheque is three
 * columns in one cell, and a new person shows up under "who knows whom".
 */
const CELL_FOR_COLUMN: Readonly<Record<string, string>> = {
  website: 'website',
  hqCountries: 'hqCountries',
  // The spreadsheet's two columns, which the Stages cell shows as its sheet source.
  stagesLed: 'stages',
  leadStatus: 'stages',
  notableInvestments: 'notableInvestments',
  chequeMin: 'cheque',
  chequeMax: 'cheque',
  chequeCurrency: 'cheque',
  sectors: 'sectors',
  investors: 'whoKnows',
};

/** The grid columns with something waiting on a person, so they can be marked. */
export function markedCells(status: ResearchStatus | undefined): ReadonlySet<string> {
  const marked = new Set<string>();
  for (const column of status?.pendingColumns ?? []) {
    const cell = CELL_FOR_COLUMN[column];
    if (cell !== undefined) marked.add(cell);
  }
  return marked;
}

const COLUMN_LABELS: Readonly<Record<string, string>> = {
  website: 'Website',
  hqCountries: 'HQ',
  stagesLed: 'Stages (spreadsheet)',
  leadStatus: 'Leads? (spreadsheet)',
  notableInvestments: 'Notable investments',
  chequeMin: 'Cheque, from',
  chequeMax: 'Cheque, to',
  chequeCurrency: 'Cheque currency',
  sectors: 'Sectors',
  investors: 'Person at the fund',
};

export function columnLabel(column: string): string {
  return COLUMN_LABELS[column] ?? column;
}

/** A proposed value as a person reads it, whatever shape the column holds. */
export function formatProposedValue(column: string, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map((item) => formatItem(column, item)).join(', ');
  return formatItem(column, value);
}

function formatItem(column: string, value: unknown): string {
  if (typeof value === 'string') {
    if (column === 'stagesLed' && value in STAGE_LABELS) {
      return STAGE_LABELS[value as keyof typeof STAGE_LABELS];
    }
    if (column === 'leadStatus' && value in LEAD_LABELS) {
      return LEAD_LABELS[value as keyof typeof LEAD_LABELS];
    }
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object' && value !== null && 'name' in value) {
    const person = value as { name: unknown; title?: unknown };
    const title = typeof person.title === 'string' ? ` — ${person.title}` : '';
    return `${String(person.name)}${title}`;
  }
  return JSON.stringify(value);
}

/** What the cell says today, so a proposal is read against it rather than alone. */
export function currentValue(fund: FundSummary, column: string): string {
  switch (column) {
    case 'website':
      return fund.website ?? '';
    case 'hqCountries':
      return fund.hqCountries.join(', ');
    case 'stagesLed':
      return fund.stagesLed.map((stage) => STAGE_LABELS[stage]).join(', ');
    case 'leadStatus':
      return LEAD_LABELS[fund.leadStatus];
    case 'notableInvestments':
      return fund.notableInvestments.join(', ');
    case 'chequeMin':
    case 'chequeMax':
    case 'chequeCurrency':
      return formatCheque(fund);
    case 'sectors':
      return fund.sectors.join(', ');
    case 'investors':
      return fund.whoKnows.map((edge) => edge.investorName).join(', ');
    default:
      return '';
  }
}
